import {createContext, ReactNode, useCallback, useEffect, useMemo, useState} from 'react'
import {useParams} from 'react-router'

import {Chats} from '../../types/graphqlSchema'

const BulkSelectionContext = createContext<BulkSelectionContextProps>(
  {} as BulkSelectionContextProps
)

const BulkSelectionContextProvider = ({children}: BulkSelectionContextProviderProps) => {
  const channelId = useParams().channelId
  const [selectedChats, setSelectedChats] = useState<Chats[]>([])
  const onToggleChatSelected = useCallback((chat: Chats) => {
    setSelectedChats(prev => prev.some(({id}) => id === chat.id) ?
      prev.filter(({id}) => id !== chat.id) :
      [...prev, chat]
    )
  }, [])
  const onClearSelection = useCallback(() => setSelectedChats([]), [])
  // Reset bulk selection only on a channel change.
  // The selection is meant to survive filter tab changes.
  useEffect(onClearSelection, [channelId, onClearSelection])
  const value = useMemo(() => ({
    isSelectionMode: !!selectedChats.length,
    onClearSelection,
    onToggleChatSelected,
    selectedChats,
  }), [onClearSelection, onToggleChatSelected, selectedChats])
  return (
    <BulkSelectionContext.Provider value={value}>
      {children}
    </BulkSelectionContext.Provider>
  )
}

interface BulkSelectionContextProps {
  isSelectionMode: boolean
  onClearSelection: VoidFunction
  onToggleChatSelected: (_: Chats) => void
  selectedChats: readonly Chats[]
}

interface BulkSelectionContextProviderProps {
  children?: ReactNode
}

export {BulkSelectionContext, BulkSelectionContextProvider}
